import { Box } from '@mui/material';
import { WorkData } from 'assets/config';
import LohaCoverWeb from 'assets/img/caseStudy/Loha/Loha-cover-web.jpg';
import LohaCoverMobile from 'assets/img/caseStudy/Loha/loha-cover-mob-.jpg';
import LohaTeam from 'assets/img/caseStudy/Loha/loha-team.jpg';
import LohaDiagram from 'assets/img/caseStudy/Loha/loha-diagram-.jpg';
import LohaLaptop from 'assets/img/caseStudy/Loha/osperb-grid-software-laptop-image-.jpg';
import LohaKunjalikuttyLaunch from 'assets/img/caseStudy/Loha/Minister-PK-Kunjalikutty-LAUNCH-OSPERB-GRID-FOR-LOHA.jpg';
import LohaDiscussion from 'assets/img/caseStudy/Loha/loha-discussin-image.jpg';

import LohaErpLaptop from 'assets/img/caseStudy/Loha/Osperb-grid-erp-laptop-image-.jpg';
import LohaWorkorder from 'assets/img/caseStudy/Loha/Osperb-grid-workorder-screen-shot-.jpg';
import LohaField from 'assets/img/caseStudy/Loha/osperb-grid-field-app-dashboard-web-.jpg';
import LohaFieldDashboard from 'assets/img/caseStudy/Loha/osperb-grid-field-dashboard-.jpg';
import LohaBanner from 'assets/img/caseStudy/Loha/LOHA-banner-all.jpg';
import LohaMockupVideo from 'assets/img/caseStudy/Loha/app-mock-up-video.mp4';

import {
  FigureWithCaption,
  BulletList,
  ListWithHeader,
  SectionDescription,
  SectionHeading,
  SectionTitle,
  LargeHeading,
  SectionBox,
  SectionMediumDescription,
  NestedListWithHeader,
} from 'components/CaseStudy/CaseStudyUtils';
import DiscussProjectSection from 'components/CaseStudy/elements/DiscussProjectSection';
import { generateCaseStudySchema, generateOrganizationSchema } from 'components/CaseStudy/SEOUtils';
import { E_COMMERCE_STYLES } from 'components/ECommerce/config';
import FeaturedCaseStudies from 'components/mobileLanding/FeaturedCaseStudies';
import VideoPlayer from 'components/VideoPlayer';
import useDevice from 'hooks/useDevice';
import { lazy, Suspense, useMemo } from 'react';
import { Helmet } from 'react-helmet';

const DefaultNavbar = lazy(() => import('../../components/DefaultNavbar'));
const MetaData = lazy(() => import('../../MetaData'));

const CSLoha = () => {
  const { screenWidth, isMobile } = useDevice();
  const PreLoad = (
    <div className="h-screen w-100 flex items-center justify-center bg-black">
      {/* <h3 className="text-osperb-blue">Osperb</h3> */}
    </div>
  );

  const featuredProjects = useMemo(() => WorkData.slice(0, 3), []);

  const caseStudySchema = useMemo(() => {
    return generateCaseStudySchema({
      title: 'Steel Window Factory Software | Quotation to Production ERP',
      description:
        'LOHA GRID case study: how Osperb built steel doors& window factory software that automates product customisation, cost-based quotation PDFs, production scheduling, and end-to-end factory workflow- eliminating owner dependency.',
      image: LohaCoverWeb,
      author: 'Osperb',
      organization: 'Osperb',
    });
  }, []);

  const organizationSchema = useMemo(() => generateOrganizationSchema(), []);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(caseStudySchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <MetaData
        title={'Steel Window Factory Software | Quotation to Production ERP'}
        content={
          'LOHA GRID case study: how Osperb built steel doors& window factory software that automates product customisation, cost-based quotation PDFs, production scheduling, and end-to-end factory workflow- eliminating owner dependency.'
        }
        description={
          'LOHA GRID case study: how Osperb built steel doors& window factory software that automates product customisation, cost-based quotation PDFs, production scheduling, and end-to-end factory workflow- eliminating owner dependency.'
        }
        keyword={
          'steel window factory software, manufacturing ERP, quotation software, production scheduling, factory management system, steel doors manufacturing software, custom window pricing, production workflow management'
        }
      />

      <Suspense fallback={PreLoad}>
        <DefaultNavbar />
      </Suspense>

      <FigureWithCaption
        src={isMobile ? LohaCoverMobile : LohaCoverWeb}
        alt="LOHA GRID steel window factory software case study - Steel doors and windows manufacturing ERP implementing product customisation module, cost calculator software, and production scheduling system by Osperb"
        title="LOHA GRID: Steel Window Factory ERP - Quotation to Production Scheduling"
        caption={
          isMobile
            ? 'Steel window factory ERP transforming quotation and production planning to automated workflow.'
            : 'LOHA GRID: Steel window factory management system transforming custom window quotation and production planning from manual process to automated ERP workflow'
        }
        mt={0}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          pt: 0,
          px: { xs: 2, sm: 3, md: 4 },
        }}
        maxWidth={E_COMMERCE_STYLES.container.maxWidthTabletLg}
        margin={E_COMMERCE_STYLES.container.mx}
      >


        <SectionBox mt={0}>
          <LargeHeading mt={0}>
            How we built steel doors and window factory ERP software for LOHA that connects
            custom-size window quotation to production scheduling and factory workflow
          </LargeHeading>

          <SectionDescription mt={{ xs: '24px', md: '40px' }}>
            LOHA is a leading steel doors and windows manufacturer in South India, handling highly
            customized steel window orders. Their biggest bottleneck was{' '}
            <b>generating quotations for custom-sized windows</b>, as staff depended on owners to
            estimate prices accurately
          </SectionDescription>

          <SectionDescription mt={{ xs: '24px', md: '40px' }}>
            Osperb built <b>LOHA GRID</b>, a <b>complete factory management system</b> with a
            product customization and custom window quotation module that calculates production cost
            using the latest raw material, consumables, and labour rates. Staff can generate
            LOHA-format quotation PDFs by adding a profit margin, without owner involvement. Once
            approved, the system moves the job the job into <b>production scheduling</b> and{' '}
            <b>end-to-end factory operations</b> tracking. LOHA GRID also includes a{' '}
            <b>field staff mobile app</b> with GPS-timestamped site photos (within a 1 km radius),
            monitor from an admin dashboard.
          </SectionDescription>

          <Box sx={{ mt: { xs: '40px', md: '72px' }, mb: { xs: '24px', md: '40px' } }}>
            <VideoPlayer
              src={'https://www.youtube.com/watch?v=jHD_RzvKPWU&t=50s'}
              autoPlay={false}
              loop={false}
              muted={true}
              controls={true}
            />
          </Box>

          <SectionBox mt={{ xs: '50px', md: '100px' }}>
            <SectionTitle variant="xs">The challenge</SectionTitle>
            <SectionHeading
              title={`The real problem wasn't quotation. It was dealers asking, "When will it be ready?" and delivery dates were being guessed based on the current workload, which became nearly impossible as orders scaled.`}
            />

            <SectionDescription>
              {`LOHA's centralised factory receives production orders from dealers across South India.`}
            </SectionDescription>

            <SectionDescription>
              {` And dealers don't just ask for price, they ask the most stressful question: `}
            </SectionDescription>

            <SectionDescription>
              <b>When will this be ready?</b>
            </SectionDescription>

            <SectionDescription>
              Because they need a completion date to confirm the order with their customer.
            </SectionDescription>

            <SectionDescription>
              {`But as the number of dealers and customized orders grew, LOHA's scheduling became a
              daily headache:`}
            </SectionDescription>

            <BulletList
              items={[
                <>
                  Orders came from everywhere, in different formats - requiring factory management
                  system
                </>,
                <>Priority kept changing through phone calls</>,
                <>
                  Production scheduling software delivery date calculation depended on manual
                  judgement
                </>,
                <>
                  The manufacturing automation system production pipeline had too many moving parts
                  to track in Excel
                </>,
              ]}
              mt="22px"
            />
            <FigureWithCaption
              src={LohaTeam}
              alt="LOHA team managing custom steel window orders before implementing manufacturing ERP"
              title="LOHA Factory Team - Production Scheduling Challenges"
              caption={
                isMobile
                  ? 'The team faced daily bottlenecks tracking production and estimating delivery dates manually.'
                  : 'As dealer orders grew across South India, the team faced daily bottlenecks tracking the manufacturing pipeline in Excel and manually estimating delivery dates.'
              }
              mt={0}
            />

          </SectionBox>

          <SectionBox mt={{ xs: '50px', md: '100px' }}>
            <SectionDescription mt="22px" sx={{ mb: 2 }}>
              At one stage,{' '}
              <b>manual scheduling and completion-date prediction became nearly impossible.</b>
            </SectionDescription>
            <SectionDescription mt={0}>
              <b>{`That's the point where LOHA reached out.`}</b>
            </SectionDescription>

            <SectionDescription>The request sounded simple:</SectionDescription>
            <SectionDescription>
              <b>{`"Build a system."`}</b>
            </SectionDescription>

            <SectionDescription sx={{ mb: '40px' }}>
              {`But once the conversations started, it was clear LOHA didn't need another app. They needed a way to run a growing factory without depending on phone calls, memory, and rough guesses.`}
            </SectionDescription>
            <Box>
              <VideoPlayer
                src={'https://www.youtube.com/shorts/8EZwEEQC2_k'}
                autoPlay={false}
                loop={false}
                muted={false}
                isReel={true}
              />
            </Box>

          </SectionBox>

          <SectionBox mt={{ xs: '50px', md: '100px' }}>

            <SectionMediumDescription sx={{ fontSize: { xs: 18, md: 24 } }}>
              {`So the first step wasn't designing screens.`}
            </SectionMediumDescription>

            <SectionHeading
              title={
                <>
                  It was <b>studying how LOHA actually works</b>
                </>
              }
              marginTop={false}
            />

            <SectionMediumDescription mt={0} sx={{ fontSize: { xs: 18, md: 24 } }}>
              {`from a dealer enquiry to a finished
                  window leaving the factory.`}
            </SectionMediumDescription>

            <SectionDescription>We spent time mapping the real flow:</SectionDescription>

            <BulletList
              items={[
                <>How dealers place orders and how staff capture requirements</>,
                <>Where specifications get missed or changed</>,
                <>How pricing decisions are made and who gets involved</>,
                <>How production currently estimates dates based on workload</>,
                <>
                  What happens inside the factory: drawing → cutting → fabrication → finishing → QC
                </>,
                <>{`Where delays happen, and where "hidden waiting time" piles up`}</>,
              ]}
              mt="22px"
            />

            <SectionDescription mt="22px">And then the pattern became obvious.</SectionDescription>

            <SectionDescription>
              {`LOHA didn't have a "quotation problem" and a "scheduling problem" separately.`}
            </SectionDescription>

            <SectionDescription>
              They had a <b>connection problem.</b>
            </SectionDescription>

            <Box sx={{ mt: '12px', ml: { xs: 0, md: 10 } }}>
              <SectionDescription>
                Specs were living in one place (WhatsApp/Excel),
                <br />
                {`pricing logic was in someone's head (owners), and`}
                <br />
                delivery dates were being guessed from workload.
                <br />
                Nothing was connected. So every dealer call created pressure, and every custom quote
                created dependency.
              </SectionDescription>
            </Box>

            <SectionDescription >
              So the plan was simple in principle, but powerful in execution:
            </SectionDescription>



            <FigureWithCaption
              src={LohaDiscussion}
              alt="LOHA team analyzing the custom steel window manufacturing workflow to build a connected factory management system"
              title="Connecting the Steel Window Manufacturing Workflow"
              caption={
                isMobile
                  ? 'Moving from scattered specs and guessed dates to a fully connected ERP.'
                  : 'Before LOHA GRID, specifications lived in WhatsApp, custom window pricing relied on the owner\'s estimates, and delivery dates were guessed. The goal was a fully connected factory ERP.'
              }
              mt={2}
            />

            <SectionBox mt="72px">
              <SectionTitle sx={{ mb: 1 }} variant="xsb">
                From Study to Solution
              </SectionTitle>
              <SectionHeading
                title={`Make one connected flow where each step automatically becomes the next step.`}
              />
              <SectionTitle sx={{ mb: 1, mt: '30px' }} variant="xsb">
                The blueprint we created
              </SectionTitle>

              <BulletList
                items={[
                  <>If the system can understand the product specs, it can calculate the cost.</>,
                  <>If it can calculate the cost, it can generate a quote.</>,
                  <>If the quote is approved, it can schedule production.</>,
                  <>
                    If production is scheduled, it can track the factory workflow and predict
                    completion.
                  </>,
                ]}
                mt="22px"
                type="none"
              />

              <SectionDescription >
                That planned solution became{' '}
                <b>LOHA GRID, not just as software, but as LOHA’s operational backbone</b>
              </SectionDescription>

              <FigureWithCaption
                src={LohaDiagram}
                alt="LOHA GRID factory management system blueprint showing how quotation, production scheduling, and end-to-end factory workflow connect"
                title="LOHA GRID: The Connected Factory Blueprint"
                caption={
                  isMobile
                    ? 'The complete factory system connecting custom quote to end-to-end operations.'
                    : 'The blueprint that became LOHA GRID—a complete factory management system connecting custom window quotation to production scheduling and end-to-end operations.'
                }
                mt={2}
              />
            </SectionBox>

            <SectionBox mt="72px">
              <SectionMediumDescription bold sx={{ fontSize: { xs: 18, md: 24 } }}>
                {`The story: how LOHA GRID was shaped step by step`}
              </SectionMediumDescription>

              <SectionHeading
                title={
                  <>
                    We Build a guided <b>product customisation module</b> where staff can configure
                    every window/door
                  </>
                }
                marginTop={false}
              />

              <SectionMediumDescription mt={2} sx={{ fontSize: { xs: 18, md: 24 } }}>
                {`Instead of allowing staff to type specs in different styles`}
              </SectionMediumDescription>

              <SectionDescription sx={{ mt: 8 }}>
                Customized steel windows/doors are not like selling a fixed product. Each order has
                dozens of variables: dimensions, channel type, grill style, mesh, hinge, lock,
                divider positions, accessories, finishes, etc.
              </SectionDescription>
              <SectionDescription sx={{ mt: 4 }}>
                If one detail is missing or misunderstood, production pays for it later—with rework,
                delay, stress, and money leakage
              </SectionDescription>
              <SectionDescription sx={{ mt: 2, }}>
                Earlier, specs came through WhatsApp texts, voice notes, and Excel rows. So staff
                used their own style to record requirements. meaning{' '}
                <b>spec consistency was never guaranteed</b>.
              </SectionDescription>

              <FigureWithCaption
                src={LohaLaptop}
                alt="Guided product customisation module on laptop replacing manual specification typing"
                title="Structured Product Customisation Flow"
                caption={
                  isMobile
                    ? 'A guided customization module replacing manual typing with structured specs.'
                    : 'A guided product customisation module where staff configure every window detail with LOHA\'s real options, ensuring total spec consistency.'
                }
                mt={2}
              />


              <SectionMediumDescription mt={10} bold sx={{ fontSize: { xs: 16, md: 18 } }}>
                What we built
              </SectionMediumDescription>
              <SectionDescription sx={{ mt: 2 }}>
                A structured <b>product customization module</b>, where staff selects specs from
                LOHA’s real options.
              </SectionDescription>
              <SectionDescription sx={{ mt: 2 }}>
                Not “free typing” but a guided specification flow.
              </SectionDescription>
              <SectionMediumDescription mt={3} bold sx={{ fontSize: { xs: 16, md: 18 } }}>
                Why it solved the problem emotionally
              </SectionMediumDescription>
              <SectionDescription sx={{ mt: 2 }}>
                Because it replaced guesswork with certainty.
              </SectionDescription>
              <SectionDescription sx={{ mt: 2, fontSize: { xs: 16, md: 16 } }}>
                Instead of a staff member thinking:
                <br /> “<b>Did I miss anything?</b>”<br />
                They now feel:
                <br /> “<b>The system won’t let me miss anything.</b>”
              </SectionDescription>
              <SectionDescription sx={{ mt: 2 }}>
                That one change reduces internal tension between sales and production dramatically.
              </SectionDescription>

              <Box sx={{ mt: '20px', mb: { xs: '24px', md: '40px' }, boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px' }}>
                <VideoPlayer
                  src={'https://youtu.be/gidH_jra7Gc'}
                  autoPlay={true}
                  loop={true}
                  muted={true}
                  controls={false}
                  pointerEvents='none'
                  aspectRatio="16/9.16"
                />
              </Box>
            </SectionBox>

            <SectionBox mt="72px">
              <SectionHeading title={`Custom Size Quote Module + Cost Engine`} />
              <SectionMediumDescription mt={3} bold sx={{ fontSize: { xs: 16, md: 18 } }}>
                “Pricing customized windows is hard…it needs real production knowledge.”
              </SectionMediumDescription>

              <BulletList
                items={[
                  <>raw material rates change</>,
                  <>accessories vary</>,
                  <>labour & consumables add up differently per design</>,
                ]}
                mt="22px"
                pl="30px"
              />

              <SectionDescription mt="22px">
                So staff depended on owners to estimate.
              </SectionDescription>

              <SectionDescription mt={3}>That created:</SectionDescription>

              <BulletList
                items={[
                  <>delays in sending quotes</>,
                  <>{`dependency culture (“only owner knows”)`}</>,
                  <>{`missed leads when customers don’t wait`}</>,
                  <>{`owner time getting consumed in repetitive tasks`}</>,
                ]}
                mt="15px"
              />

              <SectionMediumDescription mt={5} bold sx={{ fontSize: { xs: 16, md: 18 } }}>
                What we built
              </SectionMediumDescription>

              <SectionDescription>
                A <b>cost engine</b> that calculates production cost automatically using:
              </SectionDescription>

              <BulletList
                items={[
                  <>latest raw material rates</>,
                  <>consumables</>,
                  <>labour and production components</>,
                ]}
                mt="22px"
              />

              <SectionDescription mt="22px">Staff adds profit %</SectionDescription>

              <SectionDescription>Generates LOHA-format quotation PDF instantly</SectionDescription>

              <SectionDescription>
                So the calculation becomes systematic, not memory-based.
              </SectionDescription>

              <FigureWithCaption
                src={LohaKunjalikuttyLaunch}
                alt="Launch of LOHA GRID system by MLA P. K. Kunhalikutty"
                title="LOHA GRID Phase 1 Launch"
                caption={
                  isMobile
                    ? 'First phase launch by MLA P. K. Kunhalikutty on Nov 1, 2021.'
                    : 'MLA P. K. Kunhalikutty launched the first phase of the LOHA GRID factory management system on November 1, 2021.'
                }
                mt={2}
              />
            </SectionBox>

            <SectionBox mt="72px">
              <SectionMediumDescription mt={4} bold sx={{ fontSize: { xs: 16, md: 18 } }}>
                What changed in daily life
              </SectionMediumDescription>

              <SectionDescription>
                Earlier, staff had to pause and call the owner.
                <br />
                Now the staff can say confidently:
              </SectionDescription>

              <SectionMediumDescription mt={2} bold sx={{ fontSize: { xs: 16, md: 17 } }}>
                {`"Done. Here's the official quotation."`}
              </SectionMediumDescription>

              <SectionDescription>
                That one sentence changes everything in sales.
              </SectionDescription>

              <ListWithHeader
                header="For owners:"
                items={[
                  <>they stop being the human calculator</>,
                  <>they get mental space to grow the business</>,
                ]}
              />

              <SectionDescription mt="22px" >
                It turns the business from {`"owner-powered" to "system-powered."`}
              </SectionDescription>

              <FigureWithCaption
                src={LohaErpLaptop}
                alt="LOHA GRID ERP system generating official quotation on laptop"
                title="System-Powered Quotation Generation"
                caption={
                  isMobile
                    ? 'Instant quotation generation freeing owners from being human calculators.'
                    : 'With automated cost calculation, staff can instantly generate official quotes, freeing owners from daily estimation tasks.'
                }
                mt={2}
              />


            </SectionBox>

            <SectionBox mt="72px">
              <SectionHeading
                title={`Auto Production Scheduling + Completion Date Promise (the real breakthrough)`}
              />

              <SectionDescription mt={2}>
                {`Dealers didn't just ask for price, they asked the stressful question:`}
              </SectionDescription>

              <SectionDescription>
                <b>{`"When will it be ready?"`}</b>
              </SectionDescription>

              <SectionDescription>
                Earlier, the only answer came from a <b>rough guess based on current workload</b>.
                As dealer orders grew across South India, those guesses stopped working.{' '}
                <b>manual scheduling became impossible</b>.
              </SectionDescription>

              <ListWithHeader
                header="What LOHA GRID does (Capability)"
                items={[
                  <>Approved orders flow into production scheduling</>,
                  <>System suggests the next possible slot based on availability/workload</>,
                  <>Production manager confirms (human control stays)</>,
                  <>A realistic completion plan is created</>,
                ]}
              />

              <ListWithHeader
                header="Why it matters (Business benefit)"
                items={[
                  <>Dealers get predictable timelines</>,
                  <>Factory gets controlled scheduling</>,
                  <>Less phone pressure, fewer interruptions, fewer priority clashes</>,
                ]}
              />

              <SectionMediumDescription bold mt={5}>
                What changed in daily life
              </SectionMediumDescription>
              <SectionDescription>
                LOHA moved from <b>guessing delivery dates</b>
                <br /> to <b>committing dates with confidence.</b>
              </SectionDescription>

              <SectionDescription >
                {`That's a massive emotional relief for both sales and production.`}
              </SectionDescription>

              <FigureWithCaption
                src={LohaWorkorder}
                alt="Auto production scheduling and workorder screen in LOHA GRID"
                title="Auto Production Scheduling & Completion Date"
                caption={
                  isMobile
                    ? 'Automated scheduling suggesting realistic completion dates.'
                    : 'The production scheduling screen where approved orders flow seamlessly, letting the system suggest realistic completion dates instead of manual guessing.'
                }
                mt={2}
              />
            </SectionBox>

            <SectionBox mt="72px">
              <SectionHeading title="End-to-End Factory Workflow Management" marginTop={false} />

              <SectionTitle variant="xs" sx={{ mt: 2 }}>
                The pain it solved
              </SectionTitle>

              <SectionDescription>
                In many factories, work is happening, but visibility is missing.
                <br /> So the business runs on follow-ups:
              </SectionDescription>

              <BulletList
                items={[
                  <>{`"Where is this order now?"`}</>,
                  <>{`"Has cutting started?"`}</>,
                  <>{`"Is fabrication done?"`}</>,
                  <>{`"When will painting finish?"`}</>,
                ]}
                mt="22px"
              />

              <SectionDescription mt="22px">
                This follow-up culture eats time and increases stress.
              </SectionDescription>

              <ListWithHeader
                header="What LOHA GRID does (Capability)"
                items={[
                  <>Tracks production flow stage-by-stage</>,
                  <>
                    Gives operational visibility from approved order → fabrication → finish → next
                    steps
                  </>,
                ]}
              />

              <ListWithHeader
                header="Why it matters (Business benefit)"
                items={[
                  <>Fewer internal calls and confusion</>,
                  <>Less dependency on individuals</>,
                  <>Better planning for dispatch and delivery</>,
                ]}
              />

              <SectionMediumDescription bold mt={5}>
                What changed in daily life
              </SectionMediumDescription>

              <SectionDescription >
                Instead of chasing updates, teams start working with clarity.
                <br /> The factory begins to feel <b>predictable</b>, not reactive.
              </SectionDescription>

              <FigureWithCaption
                src={LohaBanner}
                alt="End-to-End Factory Workflow Management Banner for LOHA GRID"
                title="Stage-by-Stage Operational Visibility"
                caption={
                  isMobile
                    ? 'End-to-end operational visibility making factory runs predictable.'
                    : 'Full end-to-end operational visibility across all factory stages, turning a reactive environment into a predictable workflow.'
                }
                mt={1}
              />
            </SectionBox>

            <SectionBox mt="72px">
              <SectionHeading title="Field Sales Mobile App + GPS Monitoring" marginTop={false} />

              <SectionTitle sx={{ mt: 2 }} variant="xs">
                The pain it solved
              </SectionTitle>

              <SectionDescription>
                Site visits, measurements, and customer updates were hard to verify consistently.
                <br /> Admins had to rely on calls and trust-based reporting, which creates
                micromanagement pressure.
              </SectionDescription>

              <NestedListWithHeader
                header="What LOHA GRID does (Capability)"
                items={[
                  {
                    content: <>Field staff uploads site photos with:</>,
                    subItems: [
                      <>GPS</>,
                      <>timestamp</>,
                      <>validation that it was within 1km radius of the site location</>,
                    ],
                  },
                  {
                    content: <>Admin monitors activity from dashboard</>,
                  },
                ]}
              />

              {/* ShortVideo */}
              <Box sx={{ mt: '20px', mb: { xs: '24px', md: '40px' } }}>
                <VideoPlayer
                  src={'https://youtube.com/shorts/jt0Zd6dRUfU?si=DLeLmcbVIW4PB2HF'}
                  autoPlay={true}
                  loop={true}
                  muted={true}
                  isReel={true}
                  pointerEvents='none'
                />
              </Box>



              <ListWithHeader
                header="Why it matters (Business benefit)"
                items={[
                  <>Real-time field visibility</>,
                  <>Better accountability</>,
                  <>Less follow-up calls, less confusion</>,
                ]}
              />

              <SectionMediumDescription bold mt={5}>
                What changed in daily life
              </SectionMediumDescription>

              <SectionDescription>Admins stop chasing updates.</SectionDescription>

              <SectionDescription>Staff stop over-explaining.</SectionDescription>

              <SectionDescription>The system becomes the proof.</SectionDescription>
            </SectionBox>

            <FigureWithCaption
              src={LohaField}
              alt="LOHA GRID Field Sales Mobile App interface"
              title="Field Staff Mobile Application"
              caption={
                isMobile
                  ? 'Field mobile app capturing GPS and timestamped site photos.'
                  : 'The mobile app for field staff automatically captures site photos with GPS coordinates and timestamps, ensuring reliable field visibility.'
              }
              mt={2}
            />
            <FigureWithCaption
              src={LohaFieldDashboard}
              alt="LOHA GRID Admin Dashboard for monitoring field staff activity"
              title="Real-Time Field Activity Dashboard"
              caption={
                isMobile
                  ? 'Admin dashboard for real-time tracking of field sales activity.'
                  : 'The admin tracking dashboard displays GPS-verified site visits and field updates in real-time, eliminating follow-up calls.'
              }
              mt={0}
            />
          </SectionBox>

          <SectionBox mt={{ xs: '50px', md: '100px' }}>
            <LargeHeading mt={0} mb={3}>
              {`The real "after" story`}
            </LargeHeading>

            <SectionDescription>{`After LOHA GRID, LOHA didn't just get faster.`}</SectionDescription>

            <SectionDescription>
              They got <b>less dependent, more confident,</b> and <b>more predictable.</b>
            </SectionDescription>

            <SectionDescription>
              <b>Quotations became staff-run:</b> Custom-size quotes are generated instantly with
              product customisation module + auto cost calculation & steel window pricing software,
              no owner dependency.
            </SectionDescription>

            <SectionDescription>
              <b>Delivery dates became predictable:</b> Approved quotes flow into production
              scheduling software & order to production tracking, replacing rough workload guesses.
            </SectionDescription>

            <SectionDescription>
              <b>Factory operations became systemised:</b> Quote → approval → scheduling →
              production workflow runs in one connected steel window factory erp system.
            </SectionDescription>

            <SectionDescription>
              <b>Field activity became visible:</b> GPS-timestamped site photos (within 1 km) are
              tracked from an dealer order management system admin dashboard with manufacturing
              operations visibility.
            </SectionDescription>

            <SectionDescription>
              <b>Wages became transparent:</b> Post-production wages are auto-calculated and
              credited to worker wallets.
            </SectionDescription>

            <SectionDescription>{`This is what "systemisation" looks like.`}</SectionDescription>
          </SectionBox>
        </SectionBox>
      </Box>

      <DiscussProjectSection
        title={
          <>
            Need a factory
            <br />
            operations system
          </>
        }
        description="Book a 20-minute consult with Osperb and get a blueprint for your business."
        type={screenWidth > 768 ? 'web' : 'mobile'}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          pt: 10,
          px: 2,
        }}
        maxWidth={E_COMMERCE_STYLES.container.maxWidthWeb}
        margin={E_COMMERCE_STYLES.container.mx}
      >
        <FeaturedCaseStudies
          hideShadow={true}
          isSlider={isMobile ? false : true}
          type={isMobile ? 'mobile' : 'web'}
          projects={featuredProjects}
        />
      </Box>
    </>
  );
};

export default CSLoha;
